import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Dropdown,
  Input,
  message,
  Modal,
  notification,
  Spin,
  Switch,
  Table,
} from "antd";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import UserResponseModal from "../../../components/Modals/UserResponseModal";
import WidgetModal from "../../../components/Modals/WidgetModal";
import { LayoutGrid, List, Video } from "lucide-react";
import {
  QuestionCards,
  ResponseCards,
} from "../../../components/common/FormCards";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";

import {
  useDeleteQuestionMutation,
  useGetFormDetailsQuery,
  useGetFormQuestionsQuery,
  useGetFormQuestionsForSubmittedOrganizationQuery,
  useGetFormResponsesQuery,
  useGetFormResponsesForOrganizationQuery,
  useGetFormStatsByIdQuery,
  useUpdateQuestionMutation,
  useGetSubmitToOrganizationStatsQuery,
} from "../../../store/api/apiSlice";
import {
  CloseOutlined,
  MenuOutlined,
  SearchOutlined,
  MoreOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { selectCurrentRole } from "../../../store/auth/authSlice";
import { useSelector } from "react-redux";
import moment from "moment";
import StatsPopover from "./StatsTooltip";
import AddQuestionDropdown from "../../../components/Modals/AddQuestionDropdown";
import { ACTIONS_DROPDOWN_PROPS } from "../../../constants/actionsDropdown";
import { formatResponseData } from "./utils";
import StatCard from "./components/FormDetailsStatCard";
import UserGradientAvatar from "../../../components/common/UserGradientAvatar";
import {
  buildRespondentPseudonymMap,
  getInternalRespondentKey,
  getUserAvatarBackground,
} from "../../../utils/reportsHelper";

const getUserDisplayName = (user, index) => {
  const fullName =
    user?.full_name || user?.fullName || user?.name || user?.username;

  if (typeof fullName === "string" && fullName.trim()) {
    return fullName.trim();
  }

  if (typeof index === "number" && index >= 0) {
    return `User${String(index + 1).padStart(3, "0")}`;
  }

  return null;
};

const FormDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id: formId } = useParams();
  const locationState = location.state;

  const [activeTab, setActiveTab] = useState(() => {
    const q = new URLSearchParams(location.search).get("tab");
    return q === "responses" ? "responses" : "questions";
  });
  const [responseModalVisible, setResponseModalVisible] = useState(false);
  const [widgetModalVisible, setWidgetModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [respondentIdFilter, setRespondentIdFilter] = useState("");
  const [containsVideoOnly, setContainsVideoOnly] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const [viewType, setViewType] = useState(isMobile ? "grid" : "list");
  const [cachedFallbackQuestions, setCachedFallbackQuestions] = useState(null);
  const [loading, setLoading] = useState(false);
  const basePath = window.location.pathname.split("/")[1];
  const userRole = useSelector(selectCurrentRole);
  const isOrganizationUser = userRole === "organization";

  const { data: questionsDataDefault, isLoading: isLoadingDefault } =
    useGetFormQuestionsQuery(formId, {
      skip: isOrganizationUser,
    });

  const { data: questionsDataOrg, isLoading: isLoadingOrg } =
    useGetFormQuestionsForSubmittedOrganizationQuery(formId, {
      skip: !isOrganizationUser,
    });

  const {
    data: formDetailsData,
    isLoading: formDetailsLoading,
    isFetching: formDetailsFetching,
  } = useGetFormDetailsQuery(formId);
  const formData = locationState || formDetailsData?.data;

  const questionsData = isOrganizationUser
    ? questionsDataOrg?.data?.length > 0
      ? questionsDataOrg
      : cachedFallbackQuestions?.length > 0
      ? { data: cachedFallbackQuestions }
      : questionsDataOrg
    : questionsDataDefault;
  const isLoading = isOrganizationUser ? isLoadingOrg : isLoadingDefault;
  const questions = questionsData?.data || [];
  const { data: responsesDataDefault, isLoading: responsesLoadingDefault } =
    useGetFormResponsesQuery(formId, {
      skip: isOrganizationUser,
    });
  const { data: responsesDataOrg, isLoading: responsesLoadingOrg } =
    useGetFormResponsesForOrganizationQuery(formId, {
      skip: !isOrganizationUser,
    });
  const responsesData = isOrganizationUser
    ? responsesDataOrg
    : responsesDataDefault;
  const responsesLoading = isOrganizationUser
    ? responsesLoadingOrg
    : responsesLoadingDefault;

  const formattedResponses = useMemo(() => {
    const rows = formatResponseData(responsesData?.data || []);
    const pseudonymMap = buildRespondentPseudonymMap(rows);

    return rows.map((row) => {
      const respondentKey = getInternalRespondentKey(row);
      const respondentId =
        pseudonymMap[respondentKey] || getUserDisplayName();
      const avatarSeed =
        row.userId && row.userId !== "N/A" ? row.userId : row.email;

      return {
        ...row,
        respondentId,
        respondentKey,
        name: respondentId,
        avatarBackground: getUserAvatarBackground(avatarSeed),
      };
    });
  }, [responsesData?.data]);

  const filteredResponses = useMemo(() => {
    const idQuery = respondentIdFilter.trim().toLowerCase();

    return formattedResponses.filter((record) => {
      if (containsVideoOnly && !record.hasVideo) {
        return false;
      }

      if (
        idQuery &&
        !String(record.userId || "")
          .toLowerCase()
          .includes(idQuery)
      ) {
        return false;
      }

      return true;
    });
  }, [formattedResponses, respondentIdFilter, containsVideoOnly]);

  const responseTableRowCount = formattedResponses.length;
  const { data: statsData } = useGetFormStatsByIdQuery(formId);

  const { data: submitOrganizationStatsData } =
    useGetSubmitToOrganizationStatsQuery(formId);

  const overviewResponseRatio = useMemo(() => {
    const stats = isOrganizationUser
      ? submitOrganizationStatsData?.data
      : statsData?.data;
    const ratio = Number(
      stats?.response_ratio ?? stats?.response_rate ?? 0,
    );

    return Number.isFinite(ratio) ? ratio : 0;
  }, [
    isOrganizationUser,
    statsData?.data,
    submitOrganizationStatsData?.data,
  ]);

  const [deleteQuestion] = useDeleteQuestionMutation();
  const [updateQuestion] = useUpdateQuestionMutation();

  const sortedQuestions = useMemo(() => {
    if (!questions?.length) return [];
    return [...questions].sort((a, b) => a.number - b.number);
  }, [questions]);

  const handleDragEnd = async (result) => {
    if (!result.destination || activeTab !== "questions") return;

    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;
    if (sourceIndex === destinationIndex) return;

    const items = Array.from(sortedQuestions);
    const [removed] = items.splice(sourceIndex, 1);
    items.splice(destinationIndex, 0, removed);

    try {
      const updates = items.map((question, index) => {
        if (Number(question.number) === index) {
          return Promise.resolve();
        }

        return updateQuestion({
          formId,
          questionId: question._id,
          data: {
            ...question,
            number: index,
          },
        }).unwrap();
      });

      await Promise.all(updates);
    } catch (error) {
      notification.error({
        message: "Failed to update question order",
        description:
          error.message || "An error occurred while updating question order",
      });
    }
  };

  const handleDeleteQuestion = (record) => {
    const modal = Modal.confirm({
      title: "Delete Question",
      content: "Are you sure you want to delete this question?",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      confirmLoading: loading,
      onOk: async () => {
        setLoading(true);
        try {
          modal.destroy();
          await deleteQuestion({
            formId,
            questionId: record._id,
          }).unwrap();
        } catch (error) {
          message.error(error.data?.message || "Failed to delete question");
          modal.destroy();
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const goToQuestionDetails = (question) => {
    navigate(`/${basePath}/forms/${formId}/questions/details/${question._id}`, {
      state: {
        formData: formData,
        fromPath: locationState?.fromPath,
        fromTab: locationState?.fromTab,
      },
    });
  };

  const getQuestionActionMenuItems = (question) => {
    const items = [{ key: "view", label: "View Details", icon: <EyeOutlined /> }];

    if (userRole === "admin") {
      items.unshift(
        { key: "edit", label: "Edit", icon: <EditOutlined /> },
        { key: "delete", label: "Delete", danger: true, icon: <DeleteOutlined /> },
      );
    }

    return items;
  };

  const handleQuestionActionMenuClick = (question, actionKey, event) => {
    event?.domEvent?.stopPropagation?.();

    if (actionKey === "delete") return handleDeleteQuestion(question);
    if (actionKey === "edit") {
      return navigate(`/${basePath}/forms/${formId}/questions/edit/${question._id}`);
    }
    if (actionKey === "view") return goToQuestionDetails(question);
  };

  const getResponseActionsMenuItems = () => [
    { key: "view", label: "View Responses", icon: <EyeOutlined /> },
  ];

  const handleResponseActionMenuClick = (record, actionKey, event) => {
    event?.domEvent?.stopPropagation?.();

    if (actionKey === "view") {
      setSelectedUser(record);
      setResponseModalVisible(true);
    }
  };

  const columns = [
    {
      title: "Sr#",
      dataIndex: "number",
      key: "number",
      width: 60,
                                      render: (_, record, index) => index + 1,
    },
    {
      title: "Question",
      key: "question",
      render: (_, record) => (
        <div className="flex flex-col gap-1">
          <div className="font-medium text-gray-900">{record.title}</div>
          <div className="text-sm text-gray-500 truncate max-w-xl">
            {record.description}
          </div>
        </div>
      ),
    },
    {
      title: "Responses",
      key: "responses",
      render: (_, record) => (
        <span>
          {record.responses || 0} ({record.percentage || "0%"})
        </span>
      ),
    },
    {
      title: "",
      key: "actions",
      render: (_, record) => (
        <Dropdown
          {...ACTIONS_DROPDOWN_PROPS}
          menu={{
            items: getQuestionActionMenuItems(record),
            onClick: (event) =>
              handleQuestionActionMenuClick(record, event.key, event),
          }}
        >
          <button
            type="button"
            className="actions-dots-btn"
            onClick={(e) => e.stopPropagation()}
            aria-label={`Open actions for ${record?.title || "question"}`}
          >
            <MoreOutlined />
          </button>
        </Dropdown>
      ),
    },
  ];

  const responseColumns = [
    {
      title: "Sr#",
      key: "srNo",
      width: 70,
      render: (_, __, index) => index + 1,
    },
    {
      title: "User",
      key: "user",
      render: (_, record) => {
        const displayName =
          record.respondentId || record.name || getUserDisplayName();
        const displayAvatar = record.avatar || null;

        return (
          <div className="flex items-center gap-3">
            <UserGradientAvatar
              src={displayAvatar}
              alt={displayName}
              userId={record.userId || record.respondentKey}
              backgroundImageOverride={record.avatarBackground}
            />
            <div>
              <div className="font-medium text-gray-900">{displayName}</div>
              <div className="text-sm text-gray-500">{record.email}</div>
            </div>
          </div>
        );
      },
    },
    {
      title: "Respondent ID",
      dataIndex: "userId",
      key: "userId",
      width: 220,
      render: (userId) => (
        <span className="font-mono text-xs text-gray-700 break-all">
          {userId}
        </span>
      ),
    },
    {
      title: "Question",
      dataIndex: "questions",
      key: "questions",
      width: 160,
      render: (text) => <span>{text}</span>,
    },
    {
      title: "Anonymous",
      dataIndex: "anonymous",
      key: "anonymous",
      width: 100,
    },
    {
      title: "Status",
      key: "status",
      width: 120,
      render: (_, record) => (
        <span
          className={`px-3 py-2 rounded-full text-sm ${
            record.status === "Completed"
              ? "bg-green text-white"
              : "border borderred text-red-600"
          }`}
        >
          {record.status}
        </span>
      ),
    },
    {
      title: "Responses",
      key: "responses",
      width: 120,
      render: (_, record) => (
        <span>
          {record.responses} ({record.percentage})
        </span>
      ),
    },
    {
      title: "",
      key: "actions",
      width: 80,
      align: "center",
      render: (_, record) => (
        <div
          className="flex items-center justify-center"
          onClick={(e) => e.stopPropagation()}
        >
          <Dropdown
            {...ACTIONS_DROPDOWN_PROPS}
            menu={{
              items: getResponseActionsMenuItems(),
              onClick: (event) =>
                handleResponseActionMenuClick(record, event.key, event),
            }}
          >
            <button
              type="button"
              className="actions-dots-btn"
              onClick={(e) => e.stopPropagation()}
              aria-label="Open response actions"
            >
              <MoreOutlined />
            </button>
          </Dropdown>
        </div>
      ),
    },
  ];

  const handleTabChange = (tab) => {
    setActiveTab(tab);

    if (tab === "responses") {
      navigate(
        { pathname: location.pathname, search: "?tab=responses" },
        { replace: true, state: location.state },
      );
    } else {
      navigate(
        { pathname: location.pathname, search: "" },
        { replace: true, state: location.state },
      );
    }
  };

  useEffect(() => {
    const q = new URLSearchParams(location.search).get("tab");
    setActiveTab(q === "responses" ? "responses" : "questions");
  }, [location.search]);

  useEffect(() => {
    if (isOrganizationUser && formId && !cachedFallbackQuestions) {
      try {
        const stored = sessionStorage.getItem(`form_${formId}_questions`);
        if (stored) {
          const parsed = JSON.parse(stored);
          if (parsed?.length > 0) {
            setCachedFallbackQuestions(parsed);
          }
        }
      } catch (error) {
        console.warn(
          "Failed to restore cached questions from sessionStorage:",
          error,
        );
      }
    }
  }, [formId, isOrganizationUser, cachedFallbackQuestions]);

  useEffect(() => {
    if (isOrganizationUser) {
      const questionsFromLocation = locationState?.questions;
      const questionsFromFormDetails = formDetailsData?.data?.questions;
      const questionsToCache =
        questionsFromLocation || questionsFromFormDetails;

      if (
        questionsToCache?.length > 0 &&
        (!cachedFallbackQuestions || cachedFallbackQuestions.length === 0)
      ) {
        setCachedFallbackQuestions(questionsToCache);
        if (typeof window !== "undefined") {
          sessionStorage.setItem(
            `form_${formId}_questions`,
            JSON.stringify(questionsToCache),
          );
        }
      }
    }
  }, [
    isOrganizationUser,
    locationState?.questions,
    formDetailsData?.data?.questions,
    cachedFallbackQuestions,
    formId,
  ]);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (mobile) {
        setViewType("grid");
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  if (!formData) {
    const stillLoadingForm =
      formDetailsLoading || (formDetailsFetching && !formDetailsData);

    if (stillLoadingForm) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <Spin size="large" />
        </div>
      );
    }

    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900">
            Form not found
          </h2>
          <p className="text-gray-500 mt-2">
            The requested form could not be loaded.
          </p>
          <Button onClick={() => navigate("/admin/forms")} className="mt-4">
            Return to Forms
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl border p-2">
        <div className="flex justify-between items-start mb-0">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 m-0">
              {formData?.title}
            </h2>
            <p className="text-gray-500 text-sm mt-1 ">
              {formData?.description}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className="p-0 bg-yellow-300 text-yellow-800 rounded-full text-sm">
              <span
                className={`inline-flex px-3 py-1 rounded-full text-sm capitalize ${
                  formData.status === "active"
                    ? "bg-yellow text-yellow-800"
                    : formData.status === "draft"
                    ? "bg-orange text-white"
                    : "bg-red-500 text-white"
                }`}
              >
                {formData.status === "not_active"
                  ? "Inactive"
                  : formData.status}
              </span>
            </span>
          </div>
        </div>
        <div className="flex items-center justify-between gap-2 text-sm text-gray-500">
          <div>
            <span>{formData?.organization?.name}</span>
            <span>
              {formData?.organization?.description} {"/ "}
            </span>
            <span>{formData?.type}</span>
          </div>
          <div>
            <span></span>
            <span>
              {formData?.start_date &&
                formData?.end_date &&
                `${moment(formData.start_date).format(
                  "MMMM D, YYYY",
                )} - ${moment(formData.end_date).format("MMMM D, YYYY")}`}
            </span>{" "}
          </div>
        </div>
        <div className="border my-3"></div>
        <div className="flex justify-between  items-center mb-0 selectSecondary">
          <h2 className="text-xl font-semibold">Overview</h2>
        </div>
        <div className="bg-white p-0 rounded-xl">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
            <StatCard
              value={responseTableRowCount}
              label="Total Participants"
            />
            <StatCard
              value={
                isOrganizationUser
                  ? submitOrganizationStatsData?.data?.responses_received || 0
                  : statsData?.data?.total_responses || 0
              }
              label="Response Received"
            />
            <StatCard
              value={overviewResponseRatio.toFixed(1)}
              suffix="%"
              label="Response Ratio"
            />
            <StatCard
              value={
                isOrganizationUser
                  ? submitOrganizationStatsData?.data?.total_questions || 0
                  : statsData?.data?.total_questions || 0
              }
              label="Total Questions"
            />
            <StatCard
              value={
                isOrganizationUser
                  ? submitOrganizationStatsData?.data?.completion_ratio?.toFixed(
                      1,
                    ) || 0
                  : statsData?.data?.completion_ratio?.toFixed(1) || 0
              }
              suffix="%"
              label="Completion Rate"
            />
          </div>
        </div>
      </div>
      <div className="bg-white rounded-xl ">
        <div className="flex items-center justify-between mb-6 gap-2 flex-wrap">
          <div className="flex bg-secondary rounded-2xl p-1">
            <button
              onClick={() => handleTabChange("questions")}
              className={`px-4 py-2 rounded-xl border-0 text-sm ${
                activeTab === "questions" ? "bg-gray-900 text-white" : ""
              }`}
            >
              Questions
            </button>
            <button
              onClick={() => handleTabChange("responses")}
              className={`px-4 py-2 border-0 rounded-xl text-sm ${
                activeTab === "responses" ? "bg-gray-900 text-white" : ""
              }`}
            >
              Responses
            </button>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex bg-secondary rounded-xl p-1.5">
              <button
                type="button"
                className={`view-toggle-btn ${
                  viewType === "list" ? "bg-primary text-white" : ""
                }`}
                onClick={() => setViewType("list")}
              >
                <List size={20} />
              </button>
              <button
                type="button"
                className={`view-toggle-btn ${
                  viewType !== "list" ? "bg-primary text-white" : ""
                }`}
                onClick={() => setViewType("grid")}
              >
                <LayoutGrid size={20} />
              </button>
            </div>
            {userRole === "admin" && (
              <AddQuestionDropdown
                onAddNewQuestion={() =>
                  navigate(`/${basePath}/forms/${formId}/questions/add`)
                }
                formId={formId}
                questions={questions}
              />
            )}
          </div>
        </div>

        {activeTab === "responses" && (
          <div className="mb-6 rounded-2xl bg-secondary p-3 sm:p-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:max-w-4xl">
              <div className="flex min-w-0 flex-col gap-1.5">
                <label
                  htmlFor="respondent-id-filter"
                  className="text-sm font-medium text-gray-700"
                >
                  Respondent id
                </label>
                <Input
                  id="respondent-id-filter"
                  placeholder="Search by respondent ID"
                  value={respondentIdFilter}
                  onChange={(e) => setRespondentIdFilter(e.target.value)}
                  prefix={<SearchOutlined />}
                  suffix={
                    respondentIdFilter ? (
                      <CloseOutlined
                        className="cursor-pointer hover:text-gray-600"
                        onClick={() => setRespondentIdFilter("")}
                      />
                    ) : null
                  }
                  className="h-[48px] !rounded-2xl bg-white"
                />
              </div>
              <div className="flex min-w-0 flex-col gap-1.5">
                <span className="text-sm font-medium text-gray-700">
                  Video response
                </span>
                <div className="flex h-[48px] items-center justify-between gap-3 rounded-2xl border border-gray-200 bg-white px-4">
                  <div className="flex min-w-0 items-center gap-2 text-sm text-gray-700">
                    <Video
                      size={18}
                      className={
                        containsVideoOnly ? "text-emerald-600" : "text-gray-400"
                      }
                    />
                    <span className="truncate">Contains video only</span>
                  </div>
                  <Switch
                    checked={containsVideoOnly}
                    onChange={setContainsVideoOnly}
                  />
                </div>
              </div>
            </div>
            {(respondentIdFilter.trim() || containsVideoOnly) && (
              <p className="mb-0 mt-3 text-sm text-gray-500">
                Showing {filteredResponses.length} of{" "}
                {formattedResponses.length} respondents
              </p>
            )}
          </div>
        )}

        {viewType === "list" && activeTab === "questions" ? (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="droppable-questions">
              {(provided) => (
                <div
                  className="ant-table ant-table-fixed-header overflow-x-auto"
                  style={{ width: "100%" }}
                >
                  <div
                    className="ant-table-container"
                    style={{ minWidth: "800px" }}
                  >
                    <div className="ant-table-content">
                      <table
                        style={{
                          tableLayout: "fixed",
                          width: "100%",
                        }}
                      >
                        <colgroup>
                          <col style={{ width: "80px" }} />
                          <col />
                          <col style={{ width: "120px" }} />
                          <col style={{ width: "72px" }} />
                        </colgroup>
                        <thead className="ant-table-thead">
                          <tr className="mb-2">
                            <th className="text-start">Sr#</th>
                            <th className="text-start">Question</th>
                            <th className="text-end">Responses</th>
                            <th className="text-end"></th>
                          </tr>
                        </thead>
                        <tbody
                          className="ant-table-tbody"
                          {...provided.droppableProps}
                          ref={provided.innerRef}
                        >
                          {sortedQuestions.map((question, index) => (
                            <Draggable
                              key={question._id}
                              draggableId={question._id.toString()}
                              index={index}
                            >
                              {(dragProvided) => (
                                <tr
                                  ref={dragProvided.innerRef}
                                  {...dragProvided.draggableProps}
                                  className="ant-table-row ant-table-row-level-0"
                                >
                                  <td className="ant-table-cell py-2">
                                    <div className="flex items-center gap-2">
                                      <div
                                        {...dragProvided.dragHandleProps}
                                        className="cursor-grab touch-none text-gray-400 hover:text-gray-600 active:cursor-grabbing"
                                        aria-label="Drag to reorder"
                                      >
                                        <MenuOutlined />
                                      </div>
                                      <span>{index + 1}</span>
                                    </div>
                                  </td>
                                  <td className="ant-table-cell py-2 overflow-hidden">
                                    <div className="flex flex-col gap-1">
                                      <div className="font-medium text-gray-900 truncate">
                                        {question.title}
                                      </div>
                                      <div className="text-sm text-gray-500 truncate">
                                        {question.description}
                                      </div>
                                    </div>
                                  </td>
                                  <td className="ant-table-cell py-2 whitespace-nowrap text-end">
                                    <div className="flex items-center justify-end gap-2">
                                      <span>
                                        {question.count || 0} (
                                        {(question?.response_percentage &&
                                          `${question?.response_percentage?.toFixed(
                                            1,
                                          )}%`) ||
                                          "0.0%"}
                                        )
                                      </span>
                                      <StatsPopover
                                        stats={
                                          question.response?.[0]
                                            ?.statsInPercentage ||
                                          question.response?.[0]
                                            ?.numberStatistics ||
                                          question.response?.[0]?.dateStatictics
                                            ?.maxOccurrence
                                        }
                                        question={question.response}
                                      />
                                    </div>
                                  </td>
                                  <td className="ant-table-cell py-2 whitespace-nowrap text-end">
                                    <div className="flex justify-end">
                                      <Dropdown
                                        {...ACTIONS_DROPDOWN_PROPS}
                                        menu={{
                                          items: getQuestionActionMenuItems(question),
                                          onClick: (event) =>
                                            handleQuestionActionMenuClick(
                                              question,
                                              event.key,
                                              event,
                                            ),
                                        }}
                                      >
                                        <button
                                          type="button"
                                          className="actions-dots-btn"
                                          onClick={(e) => e.stopPropagation()}
                                          aria-label={`Open actions for ${
                                            question?.title || "question"
                                          }`}
                                        >
                                          <MoreOutlined />
                                        </button>
                                      </Dropdown>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </Droppable>
          </DragDropContext>
        ) : activeTab === "responses" ? (
          viewType === "list" ? (
            <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0">
              <div style={{ minWidth: "800px" }}>
                <Table
                  loading={responsesLoading}
                  columns={responseColumns}
                  dataSource={filteredResponses}
                  pagination={false}
                  className="responses-table"
                  scroll={{ x: "100%" }}
                />
              </div>
            </div>
          ) : (
            <ResponseCards
              data={filteredResponses}
              loading={responsesLoading}
              onViewResponses={(user) => {
                setSelectedUser(user);
                setResponseModalVisible(true);
              }}
            />
          )
        ) : viewType === "list" ? (
          <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0">
            <div style={{ minWidth: "800px" }}>
              <Table
                loading={isLoading}
                columns={columns}
                dataSource={sortedQuestions}
                pagination={false}
                className="form-details-table"
                scroll={{ x: "100%" }}
              />
            </div>
          </div>
        ) : (
          <QuestionCards
            data={sortedQuestions}
            navigate={navigate}
            loading={isLoading}
            onDelete={handleDeleteQuestion}
          />
        )}
      </div>
      <UserResponseModal
        visible={responseModalVisible}
        onClose={() => setResponseModalVisible(false)}
        userData={selectedUser}
        userResponses={selectedUser?.details || []}
      />
      <WidgetModal
        visible={widgetModalVisible}
        onClose={() => setWidgetModalVisible(false)}
        formId={formId}
        formTitle={formData?.title}
      />
    </div>
  );
};

export default FormDetails;
